import { useEffect, useState } from "react";
import { useRoleManagementStore } from "src/hooks/stores";
import {
  ConfirmModalContent,
  SuccessModalContent,
  FailureModalContent,
  Modal,
} from "@ucchub/ui";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { RolesTypes } from "@hub/common-libs";

type DeleteRoleModalProps = {
  show: boolean;
  onClose: () => void;
  reloadList: () => void;
  roleData: RolesTypes.RoleModel;
  isLinked: boolean;
};

const DeleteRoleModal = ({
  show,
  onClose,
  reloadList,
  roleData,
}: DeleteRoleModalProps): JSX.Element => {
  const deleteRole = useRoleManagementStore((state) => state.deleteRole.call);
  const deleteRoleConfirmMsg = useRoleManagementStore(
    (state) => state.deleteRole.error
  );
  const deleteLoading = useRoleManagementStore(
    (state) => state.deleteRole.confirmLoading
  );
  const [showFailedModal, setShowFailedModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  const [message, setMessage] = useState({
    headerMessage: "",
    subHeaderMessage: "",
  });
  const refreshRoles = useRoleManagementStore((state) => state.refreshRoles);
  const close = () => {
    onClose();
    setShowConfirmModal(false);
    setShowSuccessModal(false);
    setShowFailedModal(false);
    setShowModal(false);
    refreshRoles();
    navigate("/roles");
  };
  const { t } = useTranslation();

  const reloadRoleList = () => reloadList();

  useEffect(() => {
    if (show) {
      deleteRole(roleData, true)
        .then((res) => {
          setMessage({
            headerMessage: res.message || "",
            subHeaderMessage: res.data.message || "",
          });
          setShowConfirmModal(true);
        })
        .catch((error) => {
          setMessage({
            headerMessage: error.data.message || "",
            subHeaderMessage:
              (error.data.data && error.data.data.message) || "",
          });
          setShowFailedModal(true);
        })
        .finally(() => {
          setShowModal(true);
        });
    }
  }, [show, deleteRole, roleData]);

  const handleDeleteConfirmed = () => {
    deleteRole(roleData)
      .then((res) => {
        setMessage({
          headerMessage: res.message || "",
          subHeaderMessage: res.data.message || "",
        });
        setShowSuccessModal(true);
      })
      .catch((err) => {
        setMessage({
          headerMessage: err.message || "",
          subHeaderMessage: err.data.message || "",
        });
        setShowFailedModal(true);
      })
      .finally(() => {
        reloadRoleList();
      });
  };

  const getModalContent = () => {
    if (showFailedModal) {
      return (
        <FailureModalContent
          heading={t("core:roles:delete_role_txt")}
          message={message.headerMessage}
          body={message.subHeaderMessage}
          closeMessage={t("core:common:okay_txt") || "Okay"}
          onClose={close}
          loading={deleteLoading}
        />
      );
    }
    if (showSuccessModal) {
      return (
        <SuccessModalContent
          heading={message.headerMessage}
          body={message.subHeaderMessage}
          confirmText={t("core:roles:back_to_roles")}
          onConfirm={() => {
            refreshRoles();
            navigate("/roles");
          }}
        />
      );
    }
    if (showConfirmModal) {
      return (
        <ConfirmModalContent
          heading={t("core:roles:delete_role_txt") || "Delete role"}
          message={
            deleteRoleConfirmMsg.message || t("core:roles:delete_confirm_msg")
          }
          body={
            (deleteRoleConfirmMsg.data && deleteRoleConfirmMsg.data.message) ||
            t("core:roles:not_associated_msg")
          }
          cancelMessage={t("core:common:btn_cancel") || "Cancel"}
          onCancel={close}
          confirmMessage={t("core:common:btn_delete")}
          onConfirm={handleDeleteConfirmed}
          loading={deleteLoading}
        />
      );
    }

    return null;
  };

  return (
    <Modal
      srName="delete-role-modal"
      isOpen={showModal}
      isClosable={true}
      size={2}
      animate
      onClose={close}
    >
      <div role="dialog">{getModalContent()}</div>
    </Modal>
  );
};

export default DeleteRoleModal;
